'use strict';

import moment from "moment";
import { observable, flow, action, makeObservable } from "mobx";

import {DaysOfWeek, States} from "@uw-it-sis/lib-react/lib/AppConstants";
import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import {guid, isEmpty, isFalse} from "@uw-it-sis/lib-react/lib/Utils";

import {normalize} from "@uw-it-sis/lib-react-myplan/lib/CourseUtils";
import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";
import events from "@uw-it-sis/lib-react/lib/Events";
import PlanEvents from "@uw-it-sis/lib-react-myplan/lib/PlanEvents";
import AnnouncerStore from "@uw-it-sis/lib-react/lib/AnnouncerStore";
import {buildRegistrationSlnList} from "@uw-it-sis/lib-react-myplan/lib/RegistrationUtils";

import {convert24hour} from "./ScheduleBuilderUtils";
import scheduleBuilderDetailsStore from './ScheduleBuilderDetailsStore';
import {ErrorCodes, COURSE_CODE_WITH_SECTION_CODE_REGEX} from "./ScheduleBuilderConstants";
import PlanViewStore from "../plan/PlanViewStore";
import api from "../lib/Api";

class ScheduleBuilderPlanStore {
    state = States.initial;
    courseInclusionOptions = {};

    constructor() {
        makeObservable(this, {
            state: observable,
            courseInclusionOptions: observable,
            mount: action,
            updateCourseInclusionOptions: action
        });

        this.componentKey = "ScheduleBuilderPlan";
        this.planData = {};
        this.termId = '';
    }

    mount(termId) {
        this.termId = termId;
        events.listen(PlanEvents.planItemTypeChanged, (eo) => {
            AnnouncerStore.announce(`${eo.description} has been moved to ${eo.newType}`);
            this.loadData(this.termId);
        });
        events.listen(PlanEvents.planItemAdded, (eo) => {
            AnnouncerStore.announce(`${eo.description} has been added in ${TermUtils.makeTermDescriptionFromTermId(eo.atpId)}`);
            this.loadData(this.termId);
        });
        events.listen(PlanEvents.planItemUpdated, (eo) => {
            AnnouncerStore.announce(`${eo.description} has been updated in ${TermUtils.makeTermDescriptionFromTermId(eo.atpId)}`);
            this.loadData(this.termId);
        });
        events.listen(PlanEvents.planItemRemoved, () => this.loadData(this.termId));
        events.listen(PlanEvents.recommendedPlanItemAccepted, () => this.loadData(this.termId));
        events.listen(PlanEvents.planItemError, (eo) => this.loadData(this.termId));

        this.loadData(termId);
    }

    unmount() {
        events.remove(PlanEvents.planItemTypeChanged, (eo) => {
            AnnouncerStore.announce(`${eo.description} has been moved to ${eo.newType}`);
            this.loadData(this.termId);
        });
        events.remove(PlanEvents.planItemAdded, (eo) => {
            AnnouncerStore.announce(`${eo.description} has been added in ${TermUtils.makeTermDescriptionFromTermId(eo.atpId)}`);
            this.loadData(this.termId);
        });
        events.remove(PlanEvents.planItemUpdated, (eo) => {
            AnnouncerStore.announce(`${eo.description} has been updated in ${TermUtils.makeTermDescriptionFromTermId(eo.atpId)}`);
            this.loadData(this.termId);
        });
        events.remove(PlanEvents.planItemRemoved, () => this.loadData(this.termId));
        events.remove(PlanEvents.recommendedPlanItemAccepted, () => this.loadData(this.termId));
        events.remove(PlanEvents.planItemError, (eo) => this.loadData(this.termId));
    }

    // Fetch the plan items for the term being displayed
    loadData = flow(function* (termId) {
        this.state = States.pending;

        let terms = [];

        try {
            terms = yield api.getPlanTerms(termId, true);

            this.planData = terms[0];
            if (appStore.isDevMode()) {
                console.log(`ScheduleBuilderPlanStore: Data load complete for term ${termId}`);
            }

            this.createCourseInclusionOptions();
        } catch(error) {
            this.exceptionUtils.handleException(error, "ScheduleBuilderPlanStore.loadData");
            this.state = States.error;
        }
    }.bind(this));

    // Returns a list of plan items that are not placeholders and are schedule for the term
    getPlanData() {
        return this.planData.plannedList.filter(course => isFalse(course.placeHolder) && isFalse(course.validationIssues.hasOwnProperty("MOVE_COURSE")));
    }

    getPlanDataCount() {
        return this.getPlanData().length;
    }

    // Returns a list of course codes for planned items that are not schedule for the term, and are excluded from SB
    getNotScheduledItems () {
        return this.planData.plannedList.filter(course => course.validationIssues.hasOwnProperty("MOVE_COURSE")).map(course => course.courseDetails.code);
    }

    // Returns the term abbreviation for the term within this store
    getTermAbbr () {
        return TermUtils.makeTermAbbrFromTermId(this.planData.atpId);
    }

    /**
     * Using the list of plan items, create an object for url persistence that will have their checked/unchecked states set.
     * Items will check with current persisted data to determine if it should be checked/unchecked. If no state is found,
     * validation issues will determine checked state. Once completed, new object is passed to schedule details for persistence.
     */
    createCourseInclusionOptions() {
        let tempCourseInclusionOptions = {}
        // Update plan item list with block reg data
        let planItems = PlanViewStore.setClusterStatus(this.getPlanData());
        // Build a list of SLNs without block children
        let slnKeys = buildRegistrationSlnList(planItems);

        // Regex for course code + section code
        let codeRegex = COURSE_CODE_WITH_SECTION_CODE_REGEX;

        if (planItems.length > 0) {
            planItems.map(item => {
                // Normalize the course code for use in object keys
                let courseCode = normalize(item.courseDetails.code);

                // Check if course has a persisted checked state
                let isCourseSelected = scheduleBuilderDetailsStore.getCourseInclusionSelection(courseCode);

                // Build base of course object
                tempCourseInclusionOptions[courseCode] = {
                    selected: (isCourseSelected !== null ? isCourseSelected : true),
                    disabled: false, // default to false, unless course has a block reg child, gets updated later
                    sections: {}
                }

                let excludeActivities = false;
                // Determine if all activities should be excluded from data sent to SB
                if ( ! isEmpty(item.validationIssues) ) {
                    for (const errorCode in item.validationIssues) {
                        // If validation issues include any codes from set of error codes, exclude all activities
                        excludeActivities = ErrorCodes.hasOwnProperty(errorCode);
                        if (excludeActivities) {
                            break;
                        }
                    }
                }

                if (item.planActivities.length > 0) {
                    item.planActivities.map(activity => {
                        let excludeActivity = false;
                        if (!excludeActivities) {
                            // Determine if this activity should be excluded from data sent to SB
                            if ( ! isEmpty(activity.validationIssues) ) {
                                for (const errorCode in activity.validationIssues) {
                                    // If validation issues include any codes from set of error codes, exclude this activity
                                    excludeActivity = ErrorCodes.hasOwnProperty(errorCode);
                                    if (excludeActivity) {
                                        break;
                                    }
                                }
                            }
                        }

                        // Check if section has a persisted checked state
                        let isActivitySelected = scheduleBuilderDetailsStore.getCourseInclusionSelection(`${courseCode} ${activity.code}`);

                        // Handle block reg
                        let blockRegData = undefined;
                        if (activity.plannedBlockRegistration) {
                            // A list of block parent section codes
                            let blockParentCodes = [];

                            // Iterate over the block courses and sections, and build a list of parent section codes
                            // if the SLN is within the list of parent SLNs and in the list of SLNs that are planned.
                            activity.blockRegistration.courses.map( course => {
                                course.sections.map( section => {
                                    if ( course.parent && activity.blockRegistration.parentSlns.includes(section.sln) && slnKeys.includes(section.sln) ) {
                                        blockParentCodes.push(`${normalize(course.courseCode)} ${section.code}`);
                                    }
                                });
                            });

                            // parse the block registration tokens for a full list of course/section codes
                            // elements in the plannedBlockRegistrationData are of the form:
                            // "INFO 101 A/AE", "SOC 222 A/AC", "GEN ST 199 A2"
                            // Secondary sections are listed in the same token as the primary
                            let blockSections = [];

                            for (let blockSectionToken of activity.plannedBlockRegistrationData) {

                                blockSectionToken = blockSectionToken.trim();

                                let tempSectionCodes = [];
                                // the only valid case for having a backslash in the code is to separate the secondary sections
                                if (!blockSectionToken.includes("/")) {
                                    tempSectionCodes.push(blockSectionToken);
                                }
                                else {
                                    let slashTokens = blockSectionToken.split("/");

                                    let [fullCode, subjectArea, courseNumber, sectionCode] = slashTokens[0].match(codeRegex);

                                    tempSectionCodes.push(slashTokens[0]);
                                    for (let sectionToken of slashTokens) {
                                        if (sectionToken !== slashTokens[0]) {
                                            tempSectionCodes.push(`${subjectArea} ${courseNumber} ${sectionToken}`);
                                        }
                                    }
                                }

                                for (let tempCode of tempSectionCodes) {
                                    let [fullCode, subjectArea, courseNumber, sectionCode] = tempCode.match(codeRegex);
                                    blockSections.push({
                                        fullCode: fullCode,
                                        subjectArea: subjectArea,
                                        courseNumber: courseNumber,
                                        sectionCode: sectionCode,
                                        courseCode: `${subjectArea} ${courseNumber}`
                                    });
                                }
                            }

                            blockRegData = {
                                // set boolean if the section is a parent of a block reg
                                isParent: activity.blockRegistration.parentSlns.includes(activity.registrationCode) && slnKeys.includes(activity.registrationCode),
                                // Add a list of all the block reg sections
                                blockItems: blockSections,
                                // List of block reg parent course and section codes
                                blockParents: blockParentCodes
                            }
                        }

                        // Add the section data to the course object section prop
                        tempCourseInclusionOptions[courseCode].sections[activity.code] = {
                            // First check is if the course is selected, cant select sections without course, then use
                            // persisted state by default, then if all sections are excluded, then if just this section should be excluded
                            selected: isCourseSelected === false ? false : (isActivitySelected !== null ? isActivitySelected : (excludeActivities ? false : !excludeActivity)),
                            // disable user from changing state because section is child of block reg
                            disabled: activity.plannedBlockRegistration && !isEmpty(blockRegData) && blockRegData.hasOwnProperty("isParent") && !blockRegData.isParent,
                            blockReg: blockRegData, // include some data for later determining selected state base on the block reg
                            primary: activity.primaryActivityOfferingCode
                        };
                    });

                    // If a primary section is not selected, it's secondary should not be selected either
                    // This is a run through the section to check if a primary is unselected either by user or an issue,
                    // that its secondary is unchecked as well
                    for (const code in tempCourseInclusionOptions[courseCode].sections) {
                        if (tempCourseInclusionOptions[courseCode].sections.hasOwnProperty(code)) {
                            let primaryCode = tempCourseInclusionOptions[courseCode].sections[code].primary;
                            if (primaryCode !== code && tempCourseInclusionOptions[courseCode].sections.hasOwnProperty(primaryCode)) {
                                let isPrimarySelected = tempCourseInclusionOptions[courseCode].sections[primaryCode].selected;
                                if (!isPrimarySelected) {
                                    tempCourseInclusionOptions[courseCode].sections[code].selected = false;
                                }
                            }
                        }
                    }
                }
            });

            // Update their selected state based on the parent's selected state
            for (const courseCode of Object.keys(tempCourseInclusionOptions)) {
                for (const code in tempCourseInclusionOptions[courseCode].sections) {
                    if (tempCourseInclusionOptions[courseCode].sections.hasOwnProperty(code)) {
                        let section = tempCourseInclusionOptions[courseCode].sections[code];
                        // If section is block reg and is a parent of the block
                        if (!isEmpty(section.blockReg) && section.blockReg.isParent) {
                            let isBlockSelected = section.selected;
                            // Iterate over items within the block and update their selected state based on parent's selected state
                            for (const blockSection of section.blockReg.blockItems) {
                                // if the current section is not the block parent
                                if (blockSection.fullCode !== `${courseCode} ${code}`) {

                                    let hasAnotherSelectedParent = false;
                                    // Figure out if this child has multiple parents and their selected state
                                    // if another parent is planned and selected, we leave this child selected
                                    for ( const parentSection of tempCourseInclusionOptions[`${blockSection.subjectArea} ${blockSection.courseNumber}`].sections[blockSection.sectionCode].blockReg.blockParents) {

                                        let [parentFullCode, parentSubjectArea, parentCourseNumber, parentSectionCode] = parentSection.trim().match(codeRegex);
                                        hasAnotherSelectedParent = (
                                            parentFullCode !== `${courseCode} ${code}`
                                            &&
                                            tempCourseInclusionOptions.hasOwnProperty(`${parentSubjectArea} ${parentCourseNumber}`)
                                            &&
                                            tempCourseInclusionOptions[`${parentSubjectArea} ${parentCourseNumber}`].hasOwnProperty("sections")
                                            &&
                                            tempCourseInclusionOptions[`${parentSubjectArea} ${parentCourseNumber}`].sections.hasOwnProperty(parentSectionCode)
                                            &&
                                            tempCourseInclusionOptions[`${parentSubjectArea} ${parentCourseNumber}`].sections[parentSectionCode].selected
                                        )

                                        if (hasAnotherSelectedParent) {
                                            break;
                                        }
                                    }

                                    if (!hasAnotherSelectedParent) {
                                        // Update course selected state along with child section selected state
                                        tempCourseInclusionOptions[`${blockSection.subjectArea} ${blockSection.courseNumber}`].selected = isBlockSelected;
                                        tempCourseInclusionOptions[`${blockSection.subjectArea} ${blockSection.courseNumber}`].sections[blockSection.sectionCode].selected = isBlockSelected;
                                    }

                                    // disable the user ability to change course selection if its a child of the block
                                    if (!tempCourseInclusionOptions[courseCode].disabled) {
                                        tempCourseInclusionOptions[`${blockSection.subjectArea} ${blockSection.courseNumber}`].disabled = true;
                                    }
                                }
                            }
                        }
                    }
                }
            }

            this.courseInclusionOptions = tempCourseInclusionOptions;
        }

        this.state = States.done;
    }

    /**
     * Plan item course and section checkboxes onChange update the courseInclusionOptions
     * @param courseCd - course code of the item being updated
     * @param selected - boolean value if the item was selected or not for building SB options
     * @param activityData - object of section data, if not set, update assumes only course is updated
     */
    updateCourseInclusionOptions(courseCd, selected, activityData) {
        let courseKey = normalize(courseCd);
        const codeRegex = COURSE_CODE_WITH_SECTION_CODE_REGEX;

        // If there is no activity data, then update is for a course
        if (activityData) {
            let section = this.courseInclusionOptions[courseKey].sections[activityData.activityCd];
            // Update section selections based on args passed
            this.courseInclusionOptions[courseKey].sections[activityData.activityCd].selected = selected;
            // persist change
            scheduleBuilderDetailsStore.updateCourseInclusionSelection(`${courseKey} ${activityData.activityCd}`, selected);

            if (selected) {
                // if section is secondary, find primary and set selection flag true (can't pass secondary without primary)
                if (!activityData.primary) {
                    this.courseInclusionOptions[courseKey].sections[activityData.primaryActivityCd].selected = true;
                    // persist change
                    scheduleBuilderDetailsStore.updateCourseInclusionSelection(`${courseKey} ${activityData.primaryActivityCd}`, true);
                }

                // make sure the parent course is select if an activity is selected
                this.courseInclusionOptions[courseKey].selected = true;
                // persist change
                scheduleBuilderDetailsStore.updateCourseInclusionSelection(`${courseKey}`, true);

                // Handle block reg section coupling
                if (!isEmpty(section.blockReg) && section.blockReg.isParent) {
                    for (const blockSection of section.blockReg.blockItems) {
                        // if current block reg section is not the block parent
                        if (blockSection.fullCode !== `${courseKey} ${activityData.activityCd}`) {
                            this.courseInclusionOptions[blockSection.courseCode].selected = true;
                            scheduleBuilderDetailsStore.updateCourseInclusionSelection(blockSection.courseCode, true);
                            this.courseInclusionOptions[blockSection.courseCode].sections[blockSection.sectionCode].selected = true;
                            scheduleBuilderDetailsStore.updateCourseInclusionSelection(blockSection.fullCode, true);
                        }
                    }
                }
            } else {
                // if primary section is uncheck, uncheck all related secondaries (can't leave secondaries without primary)
                if (activityData.primary) {
                    // loop through sections
                    for (const code in this.courseInclusionOptions[courseKey].sections) {
                        // find sections who's primary matches the current primary selection's section code
                        if (this.courseInclusionOptions[courseKey].sections[code].primary === activityData.activityCd) {
                            // set flag to false if section primary matches
                            this.courseInclusionOptions[courseKey].sections[code].selected = false;
                            // persist change
                            scheduleBuilderDetailsStore.updateCourseInclusionSelection(`${courseKey} ${code}`, false);
                        }
                    }
                }

                // Handle block reg section coupling
                if (!isEmpty(section.blockReg) && section.blockReg.isParent) {
                    for (const blockSection of section.blockReg.blockItems) {
                        // if current block reg section is not the block parent
                        if (blockSection.fullCode !== `${courseKey} ${activityData.activityCd}`) {
                            let hasAnotherSelectedParent = false;
                            // Figure out if this child has multiple parents and their selected state
                            // if another parent is planned and selected, we leave this child selected
                            for ( const parentSection of this.courseInclusionOptions[blockSection.courseCode].sections[blockSection.sectionCode].blockReg.blockParents) {
                                let [parentFullCode, parentSubjectArea, parentCourseNumber, parentSectionCode] = parentSection.trim().match(codeRegex);
                                hasAnotherSelectedParent = (
                                    parentFullCode !== `${courseKey} ${activityData.activityCd}`
                                    &&
                                    this.courseInclusionOptions.hasOwnProperty(`${parentSubjectArea} ${parentCourseNumber}`)
                                    &&
                                    this.courseInclusionOptions[`${parentSubjectArea} ${parentCourseNumber}`].hasOwnProperty("sections")
                                    &&
                                    this.courseInclusionOptions[`${parentSubjectArea} ${parentCourseNumber}`].sections.hasOwnProperty(parentSectionCode)
                                    &&
                                    this.courseInclusionOptions[`${parentSubjectArea} ${parentCourseNumber}`].sections[parentSectionCode].selected
                                )

                                if (hasAnotherSelectedParent) {
                                    break;
                                }
                            }

                            if (!hasAnotherSelectedParent) {
                                this.courseInclusionOptions[blockSection.courseCode].selected = false;
                                scheduleBuilderDetailsStore.updateCourseInclusionSelection(blockSection.courseCode, false);
                                this.courseInclusionOptions[blockSection.courseCode].sections[blockSection.sectionCode].selected = false;
                                scheduleBuilderDetailsStore.updateCourseInclusionSelection(`${blockSection.fullCode}`, false);
                            }
                        }
                    }
                }
            }
        } else {
            // Update course selections based on args passed
            this.courseInclusionOptions[courseKey].selected = selected;
            // persist change
            scheduleBuilderDetailsStore.updateCourseInclusionSelection(`${courseKey}`, selected);
            // if course is unchecked, uncheck all associated sections
            if (!selected && this.courseInclusionOptions[courseKey].hasOwnProperty("sections")) {
                // loop all section and set selected to false
                for (const code in this.courseInclusionOptions[courseKey].sections) {
                    let section = this.courseInclusionOptions[courseKey].sections[code];
                    this.courseInclusionOptions[courseKey].sections[code].selected = false;
                    // persist change
                    scheduleBuilderDetailsStore.updateCourseInclusionSelection(`${courseKey} ${code}`, false);

                    // Handle block reg section coupling
                    if (!isEmpty(section.blockReg) && section.blockReg.isParent) {
                        for (const blockSection of section.blockReg.blockItems) {
                            if (blockSection.fullCode !== `${courseKey} ${code}`) {
                                this.courseInclusionOptions[blockSection.courseCode].selected = false;
                                scheduleBuilderDetailsStore.updateCourseInclusionSelection(blockSection.courseCode, false);
                                this.courseInclusionOptions[blockSection.courseCode].sections[blockSection.sectionCode].selected = false;
                                scheduleBuilderDetailsStore.updateCourseInclusionSelection(`${blockSection.fullCode}`, false);
                            }
                        }
                    }
                }
            }
        }
    }

    // Get the course or section's state of specified prop
    getCourseInclusionOption(courseCd, activityCd, prop = 'selected') {
        let courseKey = normalize(courseCd);

        // Course code does not exist in data, return null
        if (!this.courseInclusionOptions.hasOwnProperty(courseKey)) {
            return null;
        } else {
            // If section code is passed, get is for section selection state
            if (activityCd) {
                // Activity id does not exist in data, return null
                if (!this.courseInclusionOptions[courseKey].sections.hasOwnProperty(activityCd)) {
                    return null;
                } else {
                    return this.courseInclusionOptions[courseKey].sections[activityCd][prop];
                }
            } else {
                return this.courseInclusionOptions[courseKey][prop];
            }
        }
    }

    /**
     * Build a list of course inclusion filters to be passed to SB to generate schedule options.
     * Based on the list of plan items, using the persisted selections in schedule builder details,
     * if an item has been selected, its pertaining data will be added to the filter list.
     * @return - a list of course inclusion filters based on selections
     */
    getCourseInclusionFilter() {
        let planItems = this.getPlanData();
        let courseInclusionFilter = [];

        if (planItems.length > 0) {
            planItems.map(item => {
                let courseCode = normalize(item.courseDetails.code);

                // Determine if course has been selected, if not nothing for this course is added to the filter list
                let isCourseSelected = this.getCourseInclusionOption(courseCode);
                if (isCourseSelected) {
                    // Base object for course data for filter
                    let itemObject = {
                        "courseId": item.courseDetails.courseId,
                        "courseCd": item.courseDetails.code, // NOTE: dont use normalized course code, SB likes the spaces for some reason
                        "activities": []
                    };

                    if (item.planActivities.length > 0) {
                        item.planActivities.map(activity => {
                            // Determine if section has been selected
                            let isActivitySelected = this.getCourseInclusionOption(courseCode, activity.code);
                            if (isActivitySelected) {
                                // Add section object data for filter
                                itemObject.activities.push({
                                    "activityId": activity.activityId,
                                    "activityCd": activity.code,
                                    "activityType": activity.activityOfferingType,
                                    // fixme: siscloud migration: including this caused error, is this needed then?
                                    // "primary": activity.primary,
                                    "primaryActivityId": activity.primaryActivityOfferingId,
                                    "primaryActivityCd": activity.primaryActivityOfferingCode
                                });
                            }
                        });
                    }

                    // Add the course to the list of filters
                    courseInclusionFilter.push(itemObject);
                }
            });
        }

        return courseInclusionFilter;
    }

    getCourseInclusionFilterCount() {
        return this.getCourseInclusionFilter().length;
    }

    // Returns a list of registered items for schedule builder options post data
    getAcademicRecordForScheduleOptions() {
        let scheduleAcademicRecords = [];

        if ( this.hasAcademicRecordItems() ) {
            this.getAcademicRecordItems().map(item => {
                let meetingEvents = item.activityOfferingItem.meetingDetailsList.map(meeting => {
                    let tempEvent = {
                        startTime: "",
                        endTime: ""
                    };

                    let dayCodes = [];

                    if ((meeting.days === null || meeting.days === "") && (meeting.time === null || meeting.time === "")) {
                        tempEvent.allDay = true;
                    } else {
                        tempEvent.allDay = false;
                        tempEvent.startTime = meeting.time.split("-")[0].trim();
                        tempEvent.endTime = meeting.time.split("-")[1].trim();

                        dayCodes = meeting.days.split(/(?=[A-Z])/);
                    }

                    DaysOfWeek.map(day => {
                        tempEvent[day.key] = dayCodes.indexOf(day.shortCode) >= 0;
                    });

                    return tempEvent;
                });
                scheduleAcademicRecords.push({
                    "activityId": item.activityOfferingItem.activityId,
                    "activityCode": item.activityOfferingItem.code,
                    "timeMaskFlag":item.activityOfferingItem.timeMaskFlag,
                    "meetingEvents": meetingEvents
                });
            });
        }

        return scheduleAcademicRecords;
    }

    // Returns an obj of registered items in the fullcalender event object form
    getAcademicRecordAsEvents(initialDate) {
        let events = [];
        if ( this.hasAcademicRecordItems() ) {
            this.getAcademicRecordItems().map(item => {
                item.activityOfferingItem.meetingDetailsList.map(meeting => {
                    let tempEvent = {
                        title: `${item.courseCode} ${item.activityOfferingItem.code}`,
                        startRecur: initialDate,
                        endRecur: moment(initialDate, 'YYYY-MM-DD').add(6, 'days').format('YYYY-MM-DD'),
                        //Custom properties needed to build the course modal on click event
                        extendedProps: {
                            courseObj: {...item},
                            registered: true
                        }
                    };

                    if ((meeting.days === null || meeting.days === "") && (meeting.time === null || meeting.time === "")) {
                        tempEvent.allDay = true;
                    } else {
                        let dayCodes = meeting.days.split(/(?=[A-Z])/);
                        let days = DaysOfWeek.map(day => dayCodes.indexOf(day.shortCode) >= 0 ? day.index : null).filter(index => index);

                        tempEvent.startTime = convert24hour(meeting.time.split("-")[0].trim());
                        tempEvent.endTime = convert24hour(meeting.time.split("-")[1].trim());
                        tempEvent.daysOfWeek = days;
                    }

                    if (item.activityOfferingItem.hasOwnProperty("summerTerm") && item.activityOfferingItem.summerTerm) {
                        tempEvent.extendedProps.badges = [item.activityOfferingItem.summerTerm];
                    }

                    events.push(tempEvent);
                });
            });

            return ({
                id: guid(),
                events: events,
                classNames: ['scheduleBuilder-option--registered', 'cursor-pointer'],
                registered: true,
                name: "Registered"
            });
        } else {
            return false;
        }
    }

    getAcademicRecordItems() {
        // Filter out EFS items
        let filteredAcademicRecord = this.planData.academicRecord.filter(item => {
            if (item.hasOwnProperty("activityOfferingItem")) {
                // if all the props are either null, empty, false, or length is 0,
                // then its an EFS and should be filtered out of the academic record list for SB
                let isItemEmpty = Object.values(item.activityOfferingItem).every(prop => {
                    switch (typeof prop) {
                        case "object":
                            if (Array.isArray(prop)) {
                                return prop.length === 0;
                            } else {
                                return isEmpty(prop);
                            }
                        case "string":
                            return prop === "";
                        case "boolean":
                            return prop === false;
                    }
                });

                return !isItemEmpty;
            } else {
                // Do not include this item if there is no activityOfferingItem
                return false;
            }
        });

        return filteredAcademicRecord;
    }

    hasAcademicRecordItems() {
        if (this.planData.hasOwnProperty("academicRecord")) {
            return this.getAcademicRecordItems().length > 0;
        } else {
            return false;
        }
    }

    hasAcademicRecordWeekends() {
        if ( this.planData.hasOwnProperty("academicRecord") && this.hasAcademicRecordItems() ) {
            return (
                this.getAcademicRecordItems().some(item => {
                    let hasMeetings = (
                        item.hasOwnProperty("activityOfferingItem") && item.activityOfferingItem.hasOwnProperty("meetingDetailsList") && item.activityOfferingItem.meetingDetailsList.length > 0
                    );
                    if (hasMeetings) {
                        return (
                            item.activityOfferingItem.meetingDetailsList.some(meeting => {
                                if (meeting.days) {
                                    return meeting.days.includes("S");
                                } else {
                                    return false;
                                }
                            })
                        );
                    } else {
                        return false;
                    }
                })
            );
        } else {
            return false;
        }
    }
}

export default new ScheduleBuilderPlanStore();